/**
 * Shared helpers that resolve an event's room / meeting-space requirements,
 * falling back to the wizard's scalar columns (`num_hotel_rooms_needed`,
 * `meeting_rooms`, `event_dates`) when the richer JSONB columns
 * (`accommodations`, `agenda`) are empty.
 *
 * Used by both the sourcing search (filter + sidebar pre-population) and the
 * RFP confirmation guard, so the two surfaces agree on what an event "needs".
 */

import type { Events } from "@/lib/database/events";
import { getPeakRoomCount } from "@/lib/utils/accommodations";
import { formatLongWeekdayDate, readDateCountMap } from "@/lib/utils/date-count-map";

/**
 * Free-text requirement answers that mean "not needed". Compared lowercased + trimmed.
 * Broader than just `"no"` because the event-details editor stores arbitrary text
 * (e.g. "None", "TBD") while the wizard writes `"No"`.
 *
 * Shared by the `meeting_rooms` and `food_beverage_requirements` fields, which are both
 * free text the customer types and both read as "did they ask for this". Keeping one set
 * is the point: the two drifted once, and every event that answered "No" to catering was
 * being told its quotes had no catering.
 */
const MEETING_ROOMS_NEGATIVE = new Set(["no", "none", "n/a", "na", "tbd", "0"]);

/**
 * The same, plus the spellings only seen in `food_beverage_requirements`.
 *
 * Deliberately a superset rather than one shared set: `needsMeetingSpace` gates RFP send — it
 * decides whether the meeting-space section goes in the venue email and whether
 * `validate-required.ts` blocks the send — and widening its vocabulary is an RFP change, not a
 * proposals-pill change. This PR has no business moving that line, so the new tokens stay on the
 * F&B side where they were actually observed.
 */
const FOOD_AND_BEVERAGE_NEGATIVE = new Set([...MEETING_ROOMS_NEGATIVE, "n.a.", "nope", "not needed"]);

/**
 * Whether a free-text requirement answer is an explicit refusal, as opposed to merely absent.
 *
 * @param raw - The stored answer, of whatever type the column actually holds.
 * @param vocabulary - Which refusal spellings count; the two fields do not share one, see above.
 * @returns True only when the field is present and says no.
 */
function saysNo(raw: unknown, vocabulary: ReadonlySet<string>): boolean {
  if (typeof raw !== "string") return false;
  return vocabulary.has(raw.trim().toLowerCase());
}

/**
 * Peak total guestrooms for an event. Prefers the structured `accommodations`
 * JSONB; falls back to the `num_hotel_rooms_needed` scalar only when the
 * structured peak is 0 (e.g. wizard-created events).
 *
 * @param event - The event row (or null).
 * @returns The peak room count, or 0 when neither source has a value.
 */
export function resolvePeakGuestrooms(event: Events | null): number {
  if (!event) return 0;
  const peak = getPeakRoomCount(event.accommodations);
  if (peak > 0) return peak;
  return Math.max(0, event.num_hotel_rooms_needed ?? 0);
}

/**
 * Peak meeting rooms needed for an event. Prefers the structured
 * `meeting_room_requirements` breakdown (general-session room count + breakout room count, each
 * counted only when its own `needed` flag is true); falls back to the flat `num_meeting_rooms_needed`
 * scalar only when that structured total is 0 (e.g. events answered before the general-session/
 * breakout breakdown existed, or with a manually-edited legacy count never reflected there).
 *
 * @param event - The event row (or null).
 * @returns The peak meeting-room count, or 0 when neither source has a value.
 */
export function resolvePeakMeetingRooms(event: Events | null): number {
  if (!event) return 0;
  const requirements = event.meeting_room_requirements;
  const generalSessionRooms = requirements?.generalSession?.needed ? (requirements.generalSession.roomCount ?? 0) : 0;
  const breakoutRooms = requirements?.breakout?.needed ? (requirements.breakout.count ?? 0) : 0;
  const structuredTotal = generalSessionRooms + breakoutRooms;
  if (structuredTotal > 0) return structuredTotal;
  return Math.max(0, event.num_meeting_rooms_needed ?? 0);
}

/**
 * Whether the event needs guestrooms at all (from either source).
 *
 * @param event - The event row (or null).
 * @returns True when the resolved peak guestroom count is positive.
 */
export function needsAccommodations(event: Events | null): boolean {
  return resolvePeakGuestrooms(event) > 0;
}

/**
 * Whether the event has a structured agenda (vs. only the `meeting_rooms` scalar).
 *
 * @param event - The event row (or null).
 * @returns True when `agenda` is a non-empty array.
 */
export function hasStructuredMeetingSpace(event: Events | null): boolean {
  const agenda = event?.agenda;
  return Array.isArray(agenda) && agenda.length > 0;
}

/**
 * Whether the event needs meeting space (from either source). Reads a non-empty
 * agenda, or a `meeting_rooms` string that isn't a known negative value.
 *
 * @param event - The event row (or null).
 * @returns True when the event indicates a meeting-space need.
 */
export function needsMeetingSpace(event: Events | null): boolean {
  if (!event) return false;
  if (hasStructuredMeetingSpace(event)) return true;
  const raw = event.meeting_rooms;
  if (typeof raw !== "string") return false;
  const normalized = raw.trim().toLowerCase();
  return normalized.length > 0 && !MEETING_ROOMS_NEGATIVE.has(normalized);
}

/**
 * Whether the event asked for meeting space, reading a structured agenda, the
 * legacy free-text `meeting_rooms` field, and the room-count path
 * (`meeting_room_requirements` / `num_meeting_rooms_needed`).
 *
 * Two narrower signals are deliberately NOT read, both of which can only cause a
 * missed flag rather than a false one: `meeting_room_counts_by_date` (the per-day
 * grid, which {@link getMeetingRoomNotes} does send to venues), and a
 * `meeting_room_requirements.needed` flag whose room count is still 0 — reachable
 * only from the event-details editor, since the v3 wizard blocks a zero count and
 * stamps `meeting_rooms: "Yes"` anyway.
 *
 * Deliberately broader than {@link needsMeetingSpace}, which only reads the
 * first two — a wizard-created event can answer "3 meeting rooms" with no
 * agenda and no free text, and `needsMeetingSpace` calls that `false`. That
 * narrow reading is load-bearing for the RFP guard and the sourcing sidebar
 * (which key off the agenda/date range), so this is a separate helper rather
 * than a widening of it. Use this one when the question is "did the customer
 * ask for meeting space at all", e.g. flagging a proposal that came back
 * without any.
 *
 * One thing it is NOT broader about: an explicit "No"/"None" in the free-text
 * field wins over a leftover room count. Those two disagree on a handful of
 * real events (a count from an earlier answer, never cleared), and the
 * customer typing "No" is the more direct statement of intent — reading the
 * count there would flag every quoted proposal on an event that wants no
 * meeting space at all. A non-empty agenda still wins, since that is the
 * customer actively scheduling sessions.
 *
 * @param event - The event row (or null).
 * @returns True when any meeting-space signal is present.
 */
export function hasMeetingSpaceRequirement(event: Events | null): boolean {
  // `needsMeetingSpace` already covers the agenda (via `hasStructuredMeetingSpace`)
  // and affirmative free text, so an agenda has won before the negative check below.
  if (needsMeetingSpace(event)) return true;
  if (saysNoMeetingSpace(event)) return false;
  return resolvePeakMeetingRooms(event) > 0;
}

/**
 * Whether the event asked for food & beverage, from either the scalar
 * `food_beverage_requirements` field or any agenda item's `fbRequirements`.
 *
 * The single definition, called by `buildEventContext` and the sourcing-search
 * `computeInitialOptions` rather than mirrored by them. They each carried their own copy until
 * this PR, which is how they came to disagree: both still read a typed "No" as an ask, and both
 * run on the same page render as this one — so an event that declined catering had its
 * proposals flagged by one surface and not the other.
 *
 * Lives here rather than beside its callers so it is unit-testable: the proposals caller's
 * module imports `React.cache()`-wrapped Supabase fetches and cannot be loaded in a node test.
 *
 * @param event - The event row (or null).
 * @returns True when the event indicates an F&B need.
 */
export function hasFoodAndBeverageRequirement(event: Events | null): boolean {
  if (!event) return false;
  // An agenda item with catering on it is the customer actively scheduling a meal, so it wins
  // over anything typed in the scalar field — the same precedence `needsMeetingSpace` gives a
  // non-empty agenda.
  //
  // `item?.` because `agenda` is jsonb: the column guarantees an array, not what is in it, and
  // an unguarded property read on a null element would 500 the server render of every surface
  // that calls this. No such row exists across 2,484 events today.
  const scheduled =
    Array.isArray(event.agenda) &&
    event.agenda.some((item) => Array.isArray(item?.fbRequirements) && item.fbRequirements.length > 0);
  if (scheduled) return true;

  // "No", "None", "N/A", "Not needed" — an explicit refusal, not an ask. Without this every
  // quote on an event that declined catering carried "No F&B priced", which is the complaint
  // the customer already made by typing "No".
  const raw = event.food_beverage_requirements;
  if (saysNo(raw, FOOD_AND_BEVERAGE_NEGATIVE)) return false;
  return typeof raw === "string" && raw.trim().length > 0;
}

/**
 * Whether the free-text `meeting_rooms` field is an explicit negative
 * ("No", "None", "N/A"…), as opposed to merely absent.
 *
 * @param event - The event row (or null).
 * @returns True only when the field is present and says no.
 */
function saysNoMeetingSpace(event: Events | null): boolean {
  return saysNo(event?.meeting_rooms, MEETING_ROOMS_NEGATIVE);
}

/**
 * Meeting-space date range for the event. Prefers the agenda's own dates; falls
 * back to the first `event_dates` entry when the event needs meeting space but
 * has no structured agenda (e.g. wizard-created events).
 *
 * @param event - The event row (or null).
 * @returns `{ start, end }` (YYYY-MM-DD), or null when no range can be derived.
 */
export function resolveMeetingSpaceDateRange(event: Events | null): { start: string; end: string } | null {
  if (!event) return null;

  // Agenda dates win when present.
  if (Array.isArray(event.agenda) && event.agenda.length > 0) {
    // `item?.` for the same reason as `hasFoodAndBeverageRequirement`: jsonb guarantees the
    // array, not its elements, and this one is reached by `buildEventContext` on the proposals
    // page render.
    const allDates = event.agenda
      .flatMap((item) => item?.dates ?? [])
      .filter(Boolean)
      .sort();
    if (allDates.length > 0) {
      return { start: allDates[0], end: allDates[allDates.length - 1] };
    }
  }

  // Fall back to the event date range only when meeting space is actually needed.
  if (!needsMeetingSpace(event)) return null;
  const first = event.event_dates?.[0];
  if (!first) return null;
  const start = first.startDate ?? first.date;
  const end = first.endDate ?? first.date;
  if (!start || !end) return null;
  return { start, end };
}

/**
 * Format the meeting-room scalar (`num_meeting_rooms_needed`) as a human string.
 * The wizard captures meeting-room need as this scalar (added in #1530), separate
 * from the agenda sessions and the legacy free-text `meeting_rooms` field — so an
 * event with neither agenda nor free-text can still need meeting rooms.
 *
 * @param count - The `num_meeting_rooms_needed` value off the event.
 * @returns e.g. "3 meeting rooms needed", or null when unset / zero.
 */
function formatMeetingRoomsNeeded(count: number | null | undefined): string | null {
  if (typeof count !== "number" || count <= 0) return null;
  return `${count} meeting ${count === 1 ? "room" : "rooms"} needed`;
}

/**
 * Build the meeting-room "notes" lines for an event by combining the scalar
 * `num_meeting_rooms_needed` need with the legacy free-text `meeting_rooms`
 * field. Shared so the RFP-email and RFP-table paths can't drift.
 *
 * @param event - The event containing the meeting-room signals.
 * @returns Non-empty note strings (scalar need first, then free-text), possibly empty.
 */
export function getMeetingRoomNotes(event: Events): string[] {
  return [
    formatMeetingRoomsNeeded(event.num_meeting_rooms_needed),
    formatMeetingRoomCountsByDate(event.meeting_room_counts_by_date),
    event.meeting_rooms,
  ].filter(Boolean) as string[];
}

/**
 * Formats the per-day meeting-room breakdown (`events.meeting_room_counts_by_date`) as a single
 * "day: count" line list, or null when empty. Same date-count source as the enterprise RFP
 * template's `formatMeetingRoomCountsSection` — kept here (not there) since this file is the
 * shared "what does this event need" module both the legacy RFP table and the enterprise RFP
 * email draw from, and the per-day breakdown was previously only surfaced in the latter.
 *
 * @param countsByDate - Raw `events.meeting_room_counts_by_date` jsonb value.
 * @returns "Weekday, Mon D: N room(s)" lines (sorted, comma-separated), or null when empty.
 */
function formatMeetingRoomCountsByDate(countsByDate: unknown): string | null {
  const entries = Object.entries(readDateCountMap(countsByDate))
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b));

  if (entries.length === 0) return null;

  const formatted = entries
    .map(([date, count]) => `${formatLongWeekdayDate(date)}: ${count} room${count === 1 ? "" : "s"}`)
    .join(", ");

  return `Meeting rooms by day: ${formatted}`;
}
